<template>
    <span>

        <plan-card-boxes
            v-if="hasMultipleLicenses && !isLifetimeSubscriptionSelected && 'billing' === settingsPage"
            key="plan-card-boxes"
        />

        <div key="licenses" class="licenses">
            <template v-for="(license, index) in licenses">
                <template v-if="shouldDisplayLicense(license)">
                    <pro-message-box
                        :key="license.key"
                        :license="license"
                        :index="license.key"
                        :is-billing="true"
                    >
                        <template v-slot:box-text>
                            Use this key to activate the license in your WordPress site:
                        </template>
                    </pro-message-box>

                    <base-message
                        v-if="license.used_seats > license.seats"
                        :key="`error-message-${index}`"
                        :title="licenseError.title"
                    >
                        {{ licenseError.message }}
                    </base-message>

                    <user-sites
                        v-if="( hasMultipleLicenses && 'sites' === settingsPage ) || isLifetimeSubscriptionSelected"
                        :key="`active-sites-${ index }`"
                        :license="license"
                    />
                </template>
            </template>
        </div>

        <plan-card-boxes
            v-if="!hasMultipleLicenses && !isLifetimeSubscriptionSelected && 'billing' === settingsPage"
            key="plan-card-boxes"
        />

        <user-sites
            v-if="!hasMultipleLicenses && 'sites' === settingsPage && !isLifetimeSubscriptionSelected"
            key="active-sites"
            :license="subscriptionLicense"
        />

        <invoices v-if="'none' !== currentSubscription.stripe_id && 'billing' === settingsPage" key="invoices" />
    </span>
</template>

<script>
import ProMessageBox from '@/components/ProMessageBox';
import PlanCardBoxes from '@/components/Dashboard/Settings/Billing/PlanCardBoxes';
import UserSites from '@/components/Dashboard/Settings/Billing/UserSites';
import Invoices from '@/components/Dashboard/Settings/Billing/Invoices';

import { mapGetters, mapState } from 'vuex';
import { filter } from 'lodash';

export default {
    components: {
        ProMessageBox,
        PlanCardBoxes,
        UserSites,
        Invoices,
    },

    props: {
        settingsPage: {
            type: String,
            default: '',
        },
    },

    computed: {
        ...mapState([
            'currentSubscription',
            'user',
            'licenses',
        ]),

        ...mapGetters([
            'isLifetimeSubscriptionSelected',
            'totalLicenseSeats',
        ]),

        /**
         * Check if subscription has multiple licenses
         *
         * @returns {boolean} If has more than one license
         */
        hasMultipleLicenses() {
            const subscriptionId = this.currentSubscription.id;
            let licensesCount = 0;

            this.licenses.forEach(license => {
                if (license.subscription_id === subscriptionId) {
                    licensesCount++;
                }
            });

            return 1 !== licensesCount;
        },

        /**
         * Return license error message
         *
         * @returns {object} License error message
         */
        licenseError() {
            if (this.isLifetimeSubscriptionSelected) {
                const maxSites = 1 < this.totalLicenseSeats.all ? this.totalLicenseSeats.all : 'one (1)';
                const activation = 1 < this.totalLicenseSeats.all ? 'activations' : 'activation';

                return {
                    title: 'Your license has exceeded the number of site activations allowed.',
                    message: `A lifetime license allows for ${maxSites} site ${activation}. Please deactivate sites from the list below.`,
                };
            } else {
                return {
                    title: 'This license key has exceeded the number of site activations allowed',
                    message: 'Please remove sites from the list of Active Sites or add more sites to your plan.',
                };
            }
        },

        /**
         * Return current subscription license
         *
         * @returns {object}  license Current subscription license
         */
        subscriptionLicense() {
            return filter(this.licenses, { subscription_id: this.currentSubscription.id })[0];
        },
    },

    methods: {
        /**
         * @param  {object}  license  License object.
         * @returns {boolean} Whether the license should be displayed.
         */
        shouldDisplayLicense(license) {
            return 1 === this.licenses.length ||
                this.currentSubscription?.id === license.subscription_id;
        },
    },
};
</script>
